import { createContext, useContext, useState, useCallback } from 'react';

const ToastCtx = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const toast = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3000);
  }, []);

  return (
    <ToastCtx.Provider value={toast}>
      {children}
      <div className="toast-container">
        {toasts.map(t => (
          <div key={t.id} className={`toast ${t.type}`}>{t.msg}</div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

// Role context
export const RoleContext = createContext('viewer');
export const useRole = () => useContext(RoleContext);
export const useIsAdmin = () => useContext(RoleContext) === 'admin';

// Status helpers
export const STATUS_LABELS = {
  received: 'Received',
  in_repair: 'In Repair',
  done: 'Done',
  delivered: 'Delivered',
};

export const STATUS_BADGE = {
  received: 'badge-blue',
  in_repair: 'badge-yellow',
  done: 'badge-green',
  delivered: 'badge-gray',
};

export const INVOICE_STATUS_LABELS = {
  issued: 'Issued',
  sent: 'Sent',
  paid: 'Paid',
};

export const INVOICE_STATUS_BADGE = {
  issued: 'badge-yellow',
  sent: 'badge-blue',
  paid: 'badge-green',
};

export function StatusBadge({ status }) {
  return <span className={`badge ${STATUS_BADGE[status] || 'badge-gray'}`}>{STATUS_LABELS[status] || status}</span>;
}

export function InvoiceBadge({ status }) {
  return <span className={`badge ${INVOICE_STATUS_BADGE[status] || 'badge-gray'}`}>{INVOICE_STATUS_LABELS[status] || status}</span>;
}

export function Modal({ title, onClose, children, footer, width }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={width ? { maxWidth: width } : undefined}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

export function ConfirmModal({ message, onConfirm, onClose }) {
  return (
    <Modal title="Confirm" onClose={onClose} footer={
      <>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger btn-sm" onClick={onConfirm}>Delete</button>
      </>
    }>
      <p style={{ color: 'var(--text2)' }}>{message}</p>
    </Modal>
  );
}

export function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function warrantyStatus(part) {
  if (!part.warranty_months || part.warranty_months === 0) return null;
  if (!part.warranty_start) return { label: 'Pending', cls: 'warranty-none' };
  const exp = new Date(part.warranty_start);
  exp.setMonth(exp.getMonth() + part.warranty_months);
  const now = new Date();
  if (exp < now) return { label: 'Expired', cls: 'warranty-exp' };
  const days = Math.ceil((exp - now) / 86400000);
  return { label: `${days}d left`, cls: 'warranty-ok' };
}
